import { Vector2, Object3D, Quaternion, Vector3 } from "three";
import { ICameraBehavior } from "../../utils/camera-utils";
import { GenericPanControl } from "../controls/pan-controls/generic-pan-control";
import { IPanArguments } from "../controls/pan-controls/pan-control-interface";
import {
  GenericOrbitRotateControl,
  IOrbitRotateArguments,
} from "../controls/rotate-controls/generic-orbit-rotate-control";
import {
  GenericTrackballRotateControl,
  ITrackballRotateArguments,
} from "../controls/rotate-controls/generic-trackball-rotate-control";
import {
  OrthographicFixedPointZoomControl,
  IOrthographicFixedPointZoomArguments,
} from "../controls/zoom-controls/orthographic-fixed-point-zoom-control";
import { Quick3DOrthographicCamera } from "../quick3d-orthographic-camera";
import { Viewer } from "../../viewer";

export class OrthographicTrackballBehavior implements ICameraBehavior {
  private _viewer: Viewer;
  private camera: Quick3DOrthographicCamera;
  private _deltaNDC: Vector2;
  private panControl: GenericPanControl;
  private panControlArguments: IPanArguments;
  private rotateControl: GenericTrackballRotateControl;
  private rotateControlArguments: ITrackballRotateArguments;
  private targetPoint: Object3D;
  private worldOrientation: Quaternion;
  private zoomControl: OrthographicFixedPointZoomControl;
  private zoomControlArguments: IOrthographicFixedPointZoomArguments;

  public panSpeed = 0;

  public get rotateSpeed(): number {
    return this.rotateControlArguments.rotateSpeed as number;
  }
  // public get panSpeed(): number { return 0; }
  public get zoomSpeed(): number {
    return this.zoomControlArguments.zoomStep as number;
  }

  // public set panSpeed(_val: number) { }
  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  public set rotateSpeed(val: number) {
    this.rotateControlArguments.rotateSpeed = val;
  }
  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  public set zoomSpeed(val: number) {
    this.zoomControlArguments.zoomStep = val;
  }

  public setDeltaNDC(val: Vector2) {
    this._deltaNDC = val;
  }

  public alternativeWheel: boolean;

  constructor(
    camera: Quick3DOrthographicCamera,
    targetPoint: Object3D,
    worldOrientation: Quaternion,
    viewer: Viewer
  ) {
    this._viewer = viewer;
    this.camera = camera;
    this._deltaNDC = new Vector2();
    this.panControl = new GenericPanControl();
    this.panControlArguments = {} as IPanArguments;
    this.rotateControl = new GenericTrackballRotateControl();
    this.rotateControlArguments = {
      target: new Vector3(),
    } as IOrbitRotateArguments;
    this.targetPoint = targetPoint;
    this.worldOrientation = worldOrientation;
    this.zoomControl = new OrthographicFixedPointZoomControl();
    this.zoomControlArguments = { zoomStep: 0.1 };
  }

  public start(ndcX: number, ndcY: number): void {
    this._deltaNDC.set(ndcX, ndcY);
  }

  public rotate(ndcX: number, ndcY: number): void {
    this.rotateControlArguments.rotateSpeed = 5 / 3;
    this.rotateControlArguments.horizontalAngle = ndcX - this._deltaNDC.x;
    this.rotateControlArguments.verticalAngle = ndcY - this._deltaNDC.y;
    this.rotateControlArguments.target?.copy(this.targetPoint.position);

    this.targetPoint.applyQuaternion(this.worldOrientation.invert());
    this.rotateControl.rotate(this.camera, this.rotateControlArguments);
    this.targetPoint.applyQuaternion(this.worldOrientation.invert());
    this.targetPoint.updateMatrixWorld(true);
    this._deltaNDC.set(ndcX, ndcY);
  }

  public setRotateCenter(ndcX: number, ndcY: number): Vector3 {
    return;
  }

  public pan(ndcX: number, ndcY: number): void {
    const scaleX = this.camera.width / this.camera.zoom;
    const scaleY = this.camera.height / this.camera.zoom;
    this.panControlArguments.normalizedDirectionX =
      (this._deltaNDC.x - ndcX) * scaleX;
    this.panControlArguments.normalizedDirectionY =
      (this._deltaNDC.y - ndcY) * scaleY;
    this.panControlArguments.panSpeed = 0.5;
    const delta = this.panControl.pan(this.camera, this.panControlArguments);
    this.targetPoint.position.add(delta);
    this.targetPoint.updateMatrixWorld(true);
    this._deltaNDC.set(ndcX, ndcY);
  }

  public zoom(delta: number, ndcX?: number, ndcY?: number): void {
    this.zoomControlArguments.zoomDirection = (delta / Math.abs(delta)) as
      | 1
      | -1;
    this.zoomControlArguments.ndcPointX = this._deltaNDC.x;
    this.zoomControlArguments.ndcPointY = this._deltaNDC.y;
    this.zoomControl.zoom(this.camera, this.zoomControlArguments);
  }

  public getZoomDirection(ndcX: number, ndcY: number): number {
    return this._deltaNDC.y - ndcY < 0 ? 1 : -1;
  }

  public wheel(ndcX: number, ndcY: number, wheelDirection: 1 | -1): void {
    this.zoomControlArguments.zoomDirection = wheelDirection;
    this.zoomControlArguments.ndcPointX = ndcX;
    this.zoomControlArguments.ndcPointY = ndcY;
    this.zoomControlArguments.zoomStep = 0.1;
    this.zoomControl.zoom(this.camera, this.zoomControlArguments);
    this._deltaNDC.set(ndcX, ndcY);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public end(): void {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public update(): void {}

  public flatZoom(delta: 1 | -1): void {
    this.wheel(0, 0, delta);
  }

  public flatPan(ndcDeltaX: number, ndcDeltaY: number): void {
    this.pan(this._deltaNDC.x + ndcDeltaX, this._deltaNDC.y + ndcDeltaY);
  }
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public flatRotate(): void {}
}
